/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { AdminDashUser } from "@packages/sdk";
import { RoleID } from "@packages/sdk";
import type {
  ConfirmShowProps,
  HallowElementProps,
  ThemeType,
} from "@packages/ui";
import {
  Avatar,
  Form,
  Loader,
  SelectMenu,
  Skeleton,
  spacing,
  standardSchemaResolver,
  TextArea,
  TextInput,
  useAlert,
  useForm,
} from "@packages/ui";
import { useEffect, useMemo, useRef } from "react";
import type { FieldErrors } from "react-hook-form";
import type { z } from "zod";

import { SubscriptionText } from "../SubscriptionText";

const formStyles: Interpolation = {
  width: spacing.full,
};

const contentStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  justifyContent: "flex-start",
  width: spacing.full,
};

const inputStyles: Interpolation = {
  "&:disabled": { cursor: "default !important" },
  width: spacing.full,
};

const inputSkeletonStyles: Interpolation = {
  height: spacing.xxxl,
};

const ulStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.errorError,
  listStyle: "disc",
  marginLeft: spacing.m,
});
type SingleMemberFormProps = Omit<HallowElementProps<"form">, "onSubmit"> & {
  user?: Partial<AdminDashUser> & { subscription_end_date?: string };
  image_url?: AdminDashUser["image_url"];
  readOnly?: boolean;
  loading?: boolean;
  onBeforeSubmit?: (values: Partial<AdminDashUser>) => void;
  onSubmit?: (values: Partial<AdminDashUser>) => void | Promise<void>;
  schema: z.ZodObject;
  disableRoleSelect?: boolean;
};
export const SingleMemberForm = ({
  user,
  image_url,
  schema,
  onBeforeSubmit,
  onSubmit,
  loading,
  readOnly = false,
  children,
  disableRoleSelect,
  ...props
}: SingleMemberFormProps) => {
  const t = useTranslations();
  const alert = useAlert<ConfirmShowProps & { errors: FieldErrors }>({
    description: ({ errors }) => (
      <ul css={ulStyles}>
        {Object.entries(errors).map(([key, err]) => (
          <li key={key}>
            {placeholders[key]}: {err.message}
          </li>
        ))}
      </ul>
    ),
  });
  const dataRef = useRef<Partial<AdminDashUser>>(user);

  /*
   * this is because HTML forms > Radix-UI primitives technically use strings only, even for non-strings,
   * but react-hook-forms uses exact typing, so we need the form schema to match the HTML form or else
   * nothing works, but we want the actual schema to match the enum for code clarity and consistency with the backend
   */
  const formUser = useMemo(() => {
    const formUser: any = {};
    for (const prop in user) {
      if (user.hasOwnProperty(prop)) {
        formUser[prop] = user[prop]?.toString();
      }
    }
    // don't have undefined values; React hates switching from uncontrolled to controlled fields
    for (const prop of Object.keys(schema.shape)) {
      if (!formUser[prop]) formUser[prop] = "";
    }
    return formUser;
  }, [user]);

  const form = useForm({
    mode: "onSubmit",
    values: formUser,
    resolver: standardSchemaResolver(schema),
    disabled: readOnly,
  });

  useEffect(() => {
    form.watch((values) => (dataRef.current = values));
  }, [form.watch]);

  const placeholders: { [key in keyof AdminDashUser]?: string } = {
    bio: t("profile_bio"),
    email: t("admin_dash_email"),
    last_name: t("admin_dash_last_name"),
    name: t("admin_dash_first_name"),
    role_id: t("admin_dash_role"),
    username: t("profile_username"),
  };

  if (!formUser) {
    return <Loader />;
  }

  return (
    <Form
      {...form}
      formCSS={formStyles}
      formProps={{
        onSubmit: (e) => {
          onBeforeSubmit(dataRef.current);
          form.handleSubmit(onSubmit, async (errors) => {
            await alert.show({
              title: t("general_word_error"),
              errors,
            });
          })(e);
        },
        ...props,
      }}
    >
      <div css={contentStyles}>
        {readOnly && (
          <Avatar
            imgSrc={image_url}
            name={user?.name}
            size={116}
            loading={loading}
          />
        )}
        {user?.subscription_end_date && (
          <SubscriptionText
            isShowDate
            subscription_end_date={user.subscription_end_date as string}
          />
        )}
        {Object.keys(schema.shape).map((key) => {
          if (loading) {
            return <Skeleton key={key} css={inputSkeletonStyles} />;
          }
          if (key === "bio") {
            return (
              <TextArea.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.bio}
                css={inputStyles}
              />
            );
          }
          if (key === "role_id") {
            return (
              <SelectMenu.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.role_id}
                disabled={disableRoleSelect || readOnly}
                options={[
                  {
                    value: RoleID.Student.toString(),
                    label: t("admin_dash_role_student"),
                  },
                  {
                    value: RoleID.Teacher.toString(),
                    label: t("admin_dash_role_teacher"),
                  },
                ]}
              />
            );
          }
          const placeholder = placeholders[key];
          if (!placeholder) return null;
          return (
            <TextInput.Field
              key={key}
              name={key}
              control={form.control}
              type={key === "email" ? "email" : "text"}
              placeholder={placeholder}
              css={inputStyles}
            />
          );
        })}
      </div>
      {children}
    </Form>
  );
};
